import React, { useEffect, useState } from "react";
import CartItem from "./CartItem";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCart } from "../../../Redux/Customers/Cart/Action";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import RemoveShoppingCartRoundedIcon from "@mui/icons-material/RemoveShoppingCartRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import api from "../../../config/api";
import useSiteSettings from "../../../hooks/useSiteSettings";
import { LineItemListSkeleton } from "../../../components/Skeleton";
import { useAuthModal } from "../Auth/AuthModalContext";
import { usePageTitle } from "../../../utils/pageTitle";

const Cart = () => {
  usePageTitle("Your Cart");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jwt = localStorage.getItem("jwt");
  const { cart } = useSelector((store) => store);
  const [couponInput, setCouponInput] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const { openAuthModal } = useAuthModal();

  // Guests can fill a cart, but an order needs an account (orders are bound to
  // a user). Open sign-in here instead of letting them reach a checkout that
  // would 401 on every request. Their cart merges in on sign-in.
  const goToCheckout = () => {
    if (!localStorage.getItem("jwt")) {
      toast.info("Please sign in to complete your order.");
      openAuthModal();
      return;
    }
    navigate("/checkout?step=2");
  };

  useEffect(() => {
    dispatch(getCart(jwt));
  }, [dispatch, jwt]);

  const showError = (errMessage) => toast.error(errMessage);

  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  const items = cart.cartItems || [];

  const c = cart.cart || {};
  const couponDiscount = Number(c.couponDiscount || 0);
  const productDiscount =
    Number(c.totalPrice || 0) -
    (Number(c.totalDiscountedPrice || 0) + couponDiscount);

  // Delivery fee from store settings — mirrors the server's computeShipping.
  // A single flat fee store-wide, so what's shown here is what's charged.
  const settings = useSiteSettings();
  const shippingFee = Number(settings.shippingFee) || 0;
  const freeThreshold = Number(settings.freeShippingThreshold) || 0;
  const payable = Number(c.totalDiscountedPrice || 0);
  const delivery =
    shippingFee <= 0
      ? 0
      : freeThreshold > 0 && payable >= freeThreshold
      ? 0
      : shippingFee;
  const grandTotal = payable + delivery;

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponBusy(true);
    try {
      await api.put("/api/cart/apply-coupon", { code: couponInput.trim() });
      toast.success("Coupon applied");
      setCouponInput("");
      dispatch(getCart(jwt));
    } catch (e) {
      toast.error(e.response?.data?.message || "Invalid coupon");
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = async () => {
    setCouponBusy(true);
    try {
      await api.delete("/api/cart/coupon");
      dispatch(getCart(jwt));
    } catch (e) {
      /* ignore */
    } finally {
      setCouponBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fffbf2]">
      <ToastContainer position="top-right" autoClose={4000} theme="light" />

      <div className="mx-auto max-w-7xl px-4 py-8 lg:px-8">
        {cart?.loading && items.length === 0 ? (
          <div className="mx-auto max-w-7xl">
            <LineItemListSkeleton count={3} label="Loading cart" />
          </div>
        ) : items.length > 0 ? (
          <>
            <h1 className="mb-6 font-display text-4xl font-bold text-[#511d00]">
              Your Cart{" "}
              <span className="align-middle text-lg font-semibold text-[#b28585]">
                ({cart.cart?.totalItem} items)
              </span>
            </h1>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              {/* items */}
              <div className="space-y-4 lg:col-span-2">
                {items.filter(Boolean).map((item) => (
                  <CartItem
                    key={item?._id}
                    item={item}
                    showButton={true}
                    showError={showError}
                  />
                ))}
              </div>

              {/* summary */}
              <div>
                <div className="sticky top-24 rounded-2xl border border-[#eadcc0] bg-white p-6 shadow-sm">
                  <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#b28585]">
                    Price Details
                  </h2>
                  <div className="mt-4 space-y-3 text-sm">
                    <div className="flex justify-between text-[#5e3e3e]">
                      <span>Price ({cart.cart?.totalItem} items)</span>
                      <span className="font-semibold">
                        {inr(cart.cart?.totalPrice)}
                      </span>
                    </div>
                    <div className="flex justify-between text-[#5e3e3e]">
                      <span>Discount</span>
                      <span className="font-semibold text-[#2f9e69]">
                        − {inr(productDiscount)}
                      </span>
                    </div>
                    {couponDiscount > 0 && (
                      <div className="flex justify-between text-[#5e3e3e]">
                        <span>Coupon ({c.couponCode})</span>
                        <span className="font-semibold text-[#2f9e69]">
                          − {inr(couponDiscount)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-[#5e3e3e]">
                      <span>Delivery Charges</span>
                      {delivery > 0 ? (
                        <span className="font-semibold">{inr(delivery)}</span>
                      ) : (
                        <span className="font-semibold text-[#2f9e69]">Free</span>
                      )}
                    </div>
                    <div className="my-2 h-px bg-[#eadcc0]" />
                    <div className="flex justify-between text-base font-bold text-[#511d00]">
                      <span>Total Amount</span>
                      <span>{inr(grandTotal)}</span>
                    </div>
                  </div>

                  {/* coupon */}
                  <div className="mt-4">
                    {c.couponCode ? (
                      <div className="flex items-center justify-between rounded-xl border border-[#2f9e69]/40 bg-[#eaf6ef] px-3 py-2">
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-[#2f9e69]">
                          <LocalOfferRoundedIcon sx={{ fontSize: 16 }} />
                          {c.couponCode} applied
                        </span>
                        <button
                          onClick={removeCoupon}
                          disabled={couponBusy}
                          className="text-[#8a7575] hover:text-[#b85c5c]"
                          aria-label="Remove coupon"
                        >
                          <CloseRoundedIcon sx={{ fontSize: 18 }} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex overflow-hidden rounded-xl border border-[#d8c3c3]">
                        <input
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                          placeholder="Coupon code"
                          className="w-full bg-white px-3 py-2 text-sm uppercase text-[#511d00] placeholder-[#b3a3a3] focus:outline-none"
                        />
                        <button
                          onClick={applyCoupon}
                          disabled={couponBusy}
                          className="shrink-0 bg-[#5e3e3e] px-4 text-sm font-semibold text-[#f1e7da] transition-colors hover:bg-[#4a2e1f] disabled:opacity-60"
                        >
                          Apply
                        </button>
                      </div>
                    )}
                  </div>

                  {(productDiscount + couponDiscount) > 0 && (
                    <p className="mt-3 rounded-lg bg-[#eaf6ef] px-3 py-2 text-center text-xs font-semibold text-[#2f9e69]">
                      You save {inr(productDiscount + couponDiscount)} on this order
                    </p>
                  )}

                  <Button
                    onClick={goToCheckout}
                    variant="contained"
                    fullWidth
                    sx={{ mt: 3, py: 1.4, borderRadius: "999px", fontWeight: 700 }}
                  >
                    Proceed to Checkout
                  </Button>

                  <p className="mt-3 flex items-center justify-center gap-1 text-xs text-[#8a7575]">
                    <LockRoundedIcon sx={{ fontSize: 14 }} /> Secure checkout
                  </p>
                </div>
              </div>
            </div>
          </>
        ) : (
          !cart?.loading && (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="mb-5 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#f8ecd7] to-[#efdcbd] text-[#b28585] ring-1 ring-[#e0b65c]/40">
                <RemoveShoppingCartRoundedIcon sx={{ fontSize: 44 }} />
              </div>
              <h2 className="font-display text-3xl font-bold text-[#511d00]">
                Your cart is empty
              </h2>
              <p className="mt-2 max-w-sm text-sm text-[#8a7575]">
                Looks like you haven't added anything yet. Explore our royal
                wedding collection and find your perfect look.
              </p>
              <Button
                onClick={() => {
                  navigate("/");
                  window.scrollTo(0, 0);
                }}
                variant="contained"
                sx={{ mt: 4, px: 4, py: 1.2, borderRadius: "999px", fontWeight: 700 }}
              >
                Continue Shopping
              </Button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Cart;
